/**
 * The arithmetic behind the financial statements (piece 26, Reports). 🔒 Trial
 * Balance is built first and every other statement is a presentation of the
 * same aggregate, so the sums arrive here once and each statement reads them.
 *
 * 🔨 The Trial Balance fails visibly when its two columns do not sum equal.
 * That is its whole job: it is the one report whose purpose is to prove the
 * others, so a difference is stated as a refusal on the face of the report and
 * never rounded away.
 */
import { normalBalanceSign, type AccountTypeValue, type CashFlowSectionValue } from '../accounting/chart';
import { t } from '../strings';

/** One account's signed sum over a range, as the ledger stores it: a credit balance is negative. */
export interface AccountSumLike {
  accountNumber: string;
  name: string;
  type: string;
  detailType?: string | null;
  cashFlowSection?: string | null;
  isContraAccount?: boolean;
  signedSum: number;
}

/**
 * How a statement treats the accounts it is handed (decision 0850). 🔒 With
 * `keepZeroAccounts` every account handed in prints, a zero one included: the
 * caller has already chosen the set — every active account where the
 * zero-balance filter is off, or every account carrying a figure in any shown
 * column where it is on — so each column of a comparison draws the same lines.
 */
export interface StatementOptions {
  keepZeroAccounts?: boolean;
}

export interface TrialBalanceLine {
  accountNumber: string;
  name: string;
  type: string;
  debit: number;
  credit: number;
}

export interface TrialBalanceAnswer {
  lines: readonly TrialBalanceLine[];
  debitTotal: number;
  creditTotal: number;
  /** The difference in cents, zero where the columns agree. */
  difference: number;
  /** 🔒 The refusal printed on the report's face where the columns differ; null where they agree. */
  refusal: string | null;
}

/**
 * Debits and credits from the signed sums. 🔨 A signed sum is split by its own
 * sign rather than by the account's normal balance, because an asset account
 * carrying a credit balance belongs in the credit column — hiding it in the
 * debit column as a negative is how a real imbalance stops being visible.
 */
export function trialBalance(sums: readonly AccountSumLike[], options: StatementOptions = {}): TrialBalanceAnswer {
  const lines: TrialBalanceLine[] = [];
  let debitTotal = 0;
  let creditTotal = 0;
  for (const sum of sums) {
    // 🔒 The zero-balance filter off prints every account it was handed, a zero one on both sides (decision 0850).
    if (sum.signedSum === 0 && options.keepZeroAccounts !== true) continue;
    const debit = sum.signedSum > 0 ? sum.signedSum : 0;
    const credit = sum.signedSum < 0 ? -sum.signedSum : 0;
    debitTotal += debit;
    creditTotal += credit;
    lines.push({ accountNumber: sum.accountNumber, name: sum.name, type: sum.type, debit, credit });
  }
  const difference = debitTotal - creditTotal;
  return { lines, debitTotal, creditTotal, difference, refusal: difference === 0 ? null : trialBalanceRefusal(difference) };
}

/** 🔒 Visible failure, in words a person can act on: the figure, the direction and what it means. */
export function trialBalanceRefusal(difference: number): string {
  const side = difference > 0 ? 'debits' : 'credits';
  return `the trial balance does not balance: ${side} exceed the other side by ${Math.abs(difference)} cents, so every statement drawn over these postings is wrong until it is found`;
}

// ---- Profit and Loss, the Balance Sheet and the Cash Flow, in the sections a person reads ----

/**
 * One line a statement prints (decision 0763): an account, a sub-heading with no
 * figure, a total of the lines above it, or a result computed from the totals.
 * 🔒 The kind reaches the report row, which every renderer draws by (decision 0880).
 * `indent` is how far the line stands in, which is how a sub-account reads under
 * the account it rolls up to.
 */
export interface StatementLine {
  kind: 'account' | 'heading' | 'total' | 'result';
  label: string;
  /** Cents, presented by the section's own sign; null on a heading. */
  amount: number | null;
  indent: number;
  /**
   * The account the line is, where it is one (decision 0850), so the Account
   * Code and Account Description columns can be drawn apart from the name.
   */
  account?: { number: string; name: string };
  /** The account a group's *Total for* line closes, where it closes one. */
  totalFor?: { number: string; name: string };
  /**
   * 🔒 True on a negative account line inside a cost or expense section
   * (decision 1140): the account was credited, and the figure carries a
   * *(credit)* mark after it wherever it draws.
   */
  credit?: boolean;
}

/** 🔒 The mark a credited cost line carries after its figure, and the sentence the statement's foot carries beside it (decision 1140). */
export const STATEMENT_CREDIT_MARK = t('reports.creditMark');
export const STATEMENT_CREDIT_NOTE = t('reports.creditNote');

/**
 * 🔒 A cost or expense section's lines with every negative account line marked
 * as a credit (decision 1140). The figure is never re-signed: the line stays
 * negative and the section's total still subtracts it.
 */
function markCredits(section: StatementBlock): StatementBlock {
  return { ...section, lines: section.lines.map((line) => (line.kind === 'account' && line.amount !== null && line.amount < 0 ? { ...line, credit: true } : line)) };
}

/** Whether any line of a statement carries the credit mark, which is when its foot carries the sentence (decision 1140). */
export function statementHasCredit(blocks: readonly { lines: readonly { credit?: boolean; credits?: Readonly<Record<string, boolean>> }[] }[]): boolean {
  return blocks.some((section) => section.lines.some((line) => line.credit === true || Object.values(line.credits ?? {}).some(Boolean)));
}

/** One section of a statement: its heading and its lines, the section's total among them. */
export interface StatementBlock {
  heading: string;
  lines: readonly StatementLine[];
}

/** An account as a statement names it, with the account it rolls up to where it has one. */
export interface StatementAccount extends AccountSumLike {
  parentAccountNumber?: string | null;
}

const accountLabel = (sum: { accountNumber: string; name: string }): string => `${sum.accountNumber} ${sum.name}`;

/**
 * 🔒 The lines of one section with each parent's sub-accounts standing in under
 * it and a *Total for* line closing the group (decision 0763), the shape of the
 * owner's Zoho P&L, where Sales carries its sub-accounts and *Total for Sales*.
 * An account with no sub-account in the section is one line.
 */
function sectionLines(accounts: readonly (StatementAccount & { amount: number })[]): StatementLine[] {
  const numbers = new Set(accounts.map((account) => account.accountNumber));
  const childrenOf = (parent: string) => accounts.filter((account) => account.parentAccountNumber === parent);
  const lines: StatementLine[] = [];
  const walk = (account: StatementAccount & { amount: number }, indent: number) => {
    const children = childrenOf(account.accountNumber);
    const named = { number: account.accountNumber, name: account.name };
    if (children.length === 0) {
      lines.push({ kind: 'account', label: accountLabel(account), amount: account.amount, indent, account: named });
      return;
    }
    // 🔒 A parent keeps its line in every column of a comparison: a figure where it carries one, a heading where it does not.
    lines.push({ kind: account.amount === 0 ? 'heading' : 'account', label: accountLabel(account), amount: account.amount === 0 ? null : account.amount, indent, account: named });
    for (const child of children) walk(child, indent + 1);
    const total = account.amount + children.reduce((sum, child) => sum + subtreeTotal(child), 0);
    lines.push({ kind: 'total', label: `Total for ${accountLabel(account)}`, amount: total, indent, totalFor: named });
  };
  const subtreeTotal = (account: StatementAccount & { amount: number }): number => account.amount + childrenOf(account.accountNumber).reduce((sum, child) => sum + subtreeTotal(child), 0);
  for (const account of accounts) {
    // A root is an account whose parent is not in this section; its sub-accounts are drawn beneath it.
    // 🔒 A root stands one step in under its section's heading, and the section's own total stands flush with it (decision 0880).
    if (!account.parentAccountNumber || !numbers.has(account.parentAccountNumber)) walk(account, 1);
  }
  return lines;
}

/** Every account of a section that carries a balance, with its presented amount, in number order. */
function sectionAccounts(sums: readonly StatementAccount[], belongs: (sum: StatementAccount) => boolean, sign: (sum: StatementAccount) => number, options: StatementOptions = {}): (StatementAccount & { amount: number })[] {
  // 🔒 A parent with a zero balance is kept where a sub-account of it carries one, so the group has a heading to stand under.
  const carrying = sums.filter((sum) => belongs(sum) && sum.signedSum !== 0);
  const parents = new Set(carrying.map((sum) => sum.parentAccountNumber).filter((number): number is string => Boolean(number)));
  return sums
    .filter((sum) => belongs(sum) && (options.keepZeroAccounts === true || sum.signedSum !== 0 || parents.has(sum.accountNumber)))
    .map((sum) => ({ ...sum, amount: sign(sum) * sum.signedSum || 0 }))
    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
}

/** A section with its accounts, closed by *Total for* its heading, and the total. */
function block(heading: string, accounts: readonly (StatementAccount & { amount: number })[]): { block: StatementBlock; total: number } {
  const total = accounts.reduce((sum, account) => sum + account.amount, 0);
  return { block: { heading, lines: [...sectionLines(accounts), { kind: 'total', label: `Total for ${heading}`, amount: total, indent: 0 }] }, total };
}

const result = (heading: string, amount: number): StatementBlock => ({ heading, lines: [{ kind: 'result', label: heading, amount, indent: 0 }] });

/** Where an income-statement account stands: its subtype, or its type's first where it carries none. */
function incomeSubtype(sum: StatementAccount): string {
  if (sum.detailType) return sum.detailType;
  return sum.type === 'Revenue' ? 'Income' : 'OperatingExpense';
}

export interface ProfitAndLossStatement {
  blocks: readonly StatementBlock[];
  operatingIncome: number;
  costOfGoodsSold: number;
  grossProfit: number;
  operatingExpense: number;
  operatingProfit: number;
  netProfit: number;
}

/**
 * 🔒 The Profit and Loss in the sections the owner's Zoho statement reads
 * (decision 0763): Operating Income, Cost of Goods Sold, Gross Profit, Operating
 * Expense, Operating Profit, then the other income and expense, and the Net
 * Profit/Loss. 🔨 The sections read the subtypes decision 0221 gave every
 * account, so the 5000-series cost of goods sits above the gross profit rather
 * than among the expenses. Revenue reads positive and so does an expense.
 */
export function profitAndLossStatement(sums: readonly StatementAccount[], options: StatementOptions = {}): ProfitAndLossStatement {
  const revenueSign = (sum: StatementAccount) => (sum.type === 'Revenue' ? -1 : 1);
  const expenseSign = (sum: StatementAccount) => (sum.type === 'Expense' ? 1 : -1);
  const pick = (subtype: string) => (sum: StatementAccount) => (sum.type === 'Revenue' || sum.type === 'Expense') && incomeSubtype(sum) === subtype;
  const income = block('Operating Income', sectionAccounts(sums, pick('Income'), revenueSign, options));
  // 🔒 A cost or expense section marks each credited line (decision 1140); an income section never does.
  const cogs = block('Cost of Goods Sold', sectionAccounts(sums, pick('CostOfGoodsSold'), expenseSign, options));
  const grossProfit = income.total - cogs.total;
  const opex = block('Operating Expense', sectionAccounts(sums, pick('OperatingExpense'), expenseSign, options));
  const operatingProfit = grossProfit - opex.total;
  const otherIncome = block('Non Operating Income', sectionAccounts(sums, pick('OtherIncome'), revenueSign, options));
  const otherExpense = block('Non Operating Expense', sectionAccounts(sums, pick('OtherExpense'), expenseSign, options));
  const netProfit = operatingProfit + otherIncome.total - otherExpense.total;
  return {
    blocks: [income.block, markCredits(cogs.block), result('Gross Profit', grossProfit), markCredits(opex.block), result('Operating Profit', operatingProfit), otherIncome.block, markCredits(otherExpense.block), result('Net Profit/Loss', netProfit)],
    operatingIncome: income.total,
    costOfGoodsSold: cogs.total,
    grossProfit,
    operatingExpense: opex.total,
    operatingProfit,
    netProfit,
  };
}

/** The balance-sheet sections, in the order they print, each with its own subtype and the heading it reads under. */
export const BALANCE_SHEET_SECTIONS: readonly { type: AccountTypeValue; subtype: string; heading: string }[] = [
  { type: 'Asset', subtype: 'CurrentAsset', heading: 'Current Assets' },
  { type: 'Asset', subtype: 'FixedAsset', heading: 'Fixed Assets' },
  { type: 'Asset', subtype: 'OtherAsset', heading: 'Other Assets' },
  { type: 'Liability', subtype: 'CurrentLiability', heading: 'Current Liabilities' },
  { type: 'Liability', subtype: 'LongTermLiability', heading: 'Long Term Liabilities' },
];

export interface BalanceSheetStatement {
  blocks: readonly StatementBlock[];
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  /** Assets less liabilities and equity, zero where the books balance. */
  difference: number;
}

/**
 * 🔒 The Balance Sheet grouped by the subtypes decision 0221 declares (decision
 * 0763): current, fixed and other assets, current and long-term liabilities, then
 * the equity section with the equity accounts, the retained earnings the P&L
 * carried from before the range and the net income over it. 🔨 The sums are the
 * balances as at the end of the range, from inception. 🔨 A contra account reads
 * inside its section by the section's own sign, so accumulated depreciation
 * reduces the fixed assets and owner's draws reduce the equity, and each total is
 * the sum of the lines above it. An account with no subtype stands under its
 * type's last section rather than nowhere.
 */
export function balanceSheetStatement(sums: readonly StatementAccount[], equity: { retainedEarnings: number; netIncome: number }, options: StatementOptions = {}): BalanceSheetStatement {
  const subtypeOf = (sum: StatementAccount): string => {
    if (sum.detailType && BALANCE_SHEET_SECTIONS.some((section) => section.type === sum.type && section.subtype === sum.detailType)) return sum.detailType;
    return sum.type === 'Asset' ? 'OtherAsset' : 'CurrentLiability';
  };
  const blocks: StatementBlock[] = [];
  const totals = { Asset: 0, Liability: 0 };
  for (const type of ['Asset', 'Liability'] as const) {
    for (const section of BALANCE_SHEET_SECTIONS.filter((candidate) => candidate.type === type)) {
      const accounts = sectionAccounts(sums, (sum) => sum.type === type && subtypeOf(sum) === section.subtype, () => normalBalanceSign(type, false), options);
      if (accounts.length === 0) continue;
      const built = block(section.heading, accounts);
      blocks.push(built.block);
      totals[type] += built.total;
    }
    blocks.push(result(type === 'Asset' ? 'Total Assets' : 'Total Liabilities', totals[type]));
  }
  const equityAccounts = sectionAccounts(sums, (sum) => sum.type === 'Equity', () => normalBalanceSign('Equity', false), options);
  const equityLines: StatementLine[] = [
    ...sectionLines(equityAccounts),
    { kind: 'account', label: 'Retained Earnings (computed)', amount: equity.retainedEarnings, indent: 1 },
    { kind: 'account', label: 'Net Income for the range', amount: equity.netIncome, indent: 1 },
  ];
  const totalEquity = equityAccounts.reduce((sum, account) => sum + account.amount, 0) + equity.retainedEarnings + equity.netIncome;
  blocks.push({ heading: 'Equity', lines: [...equityLines, { kind: 'total', label: 'Total for Equity', amount: totalEquity, indent: 0 }] });
  blocks.push(result('Total Liabilities and Equity', totals.Liability + totalEquity));
  return { blocks, totalAssets: totals.Asset, totalLiabilities: totals.Liability, totalEquity, difference: totals.Asset - totals.Liability - totalEquity };
}

// ---- Cash Flow, the indirect method ----

/** The three sections the indirect statement prints, in order; Excluded is not a section and never draws. */
export const CASH_FLOW_REPORT_SECTIONS: readonly CashFlowSectionValue[] = ['Operating', 'Investing', 'Financing'];

export const CASH_FLOW_SECTION_LABELS: Readonly<Record<string, string>> = {
  Operating: 'Operating activities',
  Investing: 'Investing activities',
  Financing: 'Financing activities',
  Excluded: 'Excluded from the cash flow statement',
};

/**
 * 🔨 Where an account's movement lands on the cash flow statement is the
 * account's own `cashFlowSection` and nothing else (decision 0221). A report
 * that guessed from the account type would put a bank loan and a bank account
 * in the same place.
 */
export function cashFlowPlacement(section: string | null | undefined): CashFlowSectionValue | null {
  if (section === 'Operating' || section === 'Investing' || section === 'Financing') return section;
  return null;
}

/**
 * 🔒 Cash is an asset account whose cash flow section is Excluded: its movement
 * is what the statement explains, so it is never a line on it (decision 0221).
 */
export function isCashAccount(sum: { type: string; cashFlowSection?: string | null }): boolean {
  return sum.type === 'Asset' && sum.cashFlowSection === 'Excluded';
}

export interface CashFlowStatement {
  blocks: readonly StatementBlock[];
  beginningCash: number;
  netIncome: number;
  operating: number;
  investing: number;
  financing: number;
  netChange: number;
  /** Beginning cash plus the cash accounts' own movement over the range. */
  endingCash: number;
  /** Every non-cash account whose section is Excluded or unset, named rather than dropped silently. */
  excluded: readonly { accountNumber: string; name: string; amount: number }[];
}

/**
 * 🔒 The Cash Flow on the indirect method, in the owner's Zoho statement's lines
 * (decision 0763): Beginning Cash Balance; Cash Flow from Operating Activities —
 * each working-capital account's movement, Net Income, the non-cash adjustments
 * with their total — then Net cash provided by Operating Activities; Investing and
 * Financing each with its net line; Net Change in cash; Ending Cash Balance.
 *
 * 🔨 It starts at net income and adjusts by every non-cash balance-sheet
 * account's movement, placed by the account's own `cashFlowSection` (decision
 * 0221). An account's cash effect is the negative of its signed movement: an
 * asset that grew took cash, a liability that grew gave it. 🔨 A contra asset in
 * Operating — accumulated depreciation — is a non-cash adjustment, the expense
 * added back. Revenue and expense accounts reach the statement through net
 * income alone.
 */
export function cashFlowStatement(rangeSums: readonly StatementAccount[], beginningCash: number, options: StatementOptions = {}): CashFlowStatement {
  const excluded: { accountNumber: string; name: string; amount: number }[] = [];
  const bySection: Record<CashFlowSectionValue, (StatementAccount & { amount: number })[]> = { Operating: [], Investing: [], Financing: [], Excluded: [] };
  const adjustments: (StatementAccount & { amount: number })[] = [];
  let netIncome = 0;
  let cashMovement = 0;
  for (const sum of [...rangeSums].sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))) {
    if (sum.signedSum === 0 && options.keepZeroAccounts !== true) continue;
    if (sum.type === 'Revenue' || sum.type === 'Expense') {
      netIncome -= sum.signedSum;
      continue;
    }
    if (isCashAccount(sum)) {
      cashMovement += sum.signedSum;
      continue;
    }
    const line = { ...sum, amount: -sum.signedSum || 0 };
    const placement = cashFlowPlacement(sum.cashFlowSection);
    if (placement === null) {
      // An account outside the statement is named only where it moved; a zero one explains nothing and hides nothing.
      if (sum.signedSum !== 0) excluded.push({ accountNumber: sum.accountNumber, name: sum.name, amount: line.amount });
      continue;
    }
    if (placement === 'Operating' && sum.type === 'Asset' && sum.isContraAccount === true) adjustments.push(line);
    else bySection[placement].push(line);
  }
  const accountLine = (account: StatementAccount & { amount: number }): StatementLine => ({ kind: 'account', label: accountLabel(account), amount: account.amount, indent: 1, account: { number: account.accountNumber, name: account.name } });
  const sumOf = (rows: readonly { amount: number }[]) => rows.reduce((total, row) => total + row.amount, 0);
  const adjustmentsTotal = sumOf(adjustments);
  const operating = sumOf(bySection.Operating) + netIncome + adjustmentsTotal;
  const investing = sumOf(bySection.Investing);
  const financing = sumOf(bySection.Financing);
  const netChange = operating + investing + financing;
  const blocks: StatementBlock[] = [
    result('Beginning Cash Balance', beginningCash),
    {
      heading: 'Cash Flow from Operating Activities',
      lines: [
        ...bySection.Operating.map(accountLine),
        { kind: 'account', label: 'Net Income', amount: netIncome, indent: 1 },
        { kind: 'heading', label: 'Non-cash adjustments', amount: null, indent: 1 },
        ...adjustments.map((account) => ({ ...accountLine(account), indent: 2 })),
        { kind: 'total', label: 'Non-cash adjustments Total', amount: adjustmentsTotal, indent: 1 },
        { kind: 'result', label: 'Net cash provided by Operating Activities', amount: operating, indent: 0 },
      ],
    },
    { heading: 'Cash Flow from Investing Activities', lines: [...bySection.Investing.map(accountLine), { kind: 'result', label: 'Net cash provided by Investing Activities', amount: investing, indent: 0 }] },
    { heading: 'Cash Flow from Financing Activities', lines: [...bySection.Financing.map(accountLine), { kind: 'result', label: 'Net cash provided by Financing Activities', amount: financing, indent: 0 }] },
    result('Net Change in cash', netChange),
    result('Ending Cash Balance', beginningCash + cashMovement),
  ];
  return { blocks, beginningCash, netIncome, operating, investing, financing, netChange, endingCash: beginningCash + cashMovement, excluded };
}

// ---- a statement over several columns (decision 0850) ----

/** One line of a statement drawn over several columns: its figure per column key, null where it is a heading there. */
export interface ColumnedStatementLine extends Omit<StatementLine, 'amount' | 'credit'> {
  amounts: Readonly<Record<string, number | null>>;
  /** 🔒 The columns whose figure carries the credit mark (decision 1140); each column reads its own sign. */
  credits?: Readonly<Record<string, boolean>>;
}

export interface ColumnedStatementBlock {
  heading: string;
  lines: readonly ColumnedStatementLine[];
}

/**
 * 🔒 One statement computed once per column and laid side by side (decision
 * 0850). Every column was built over the same accounts, so each has the same
 * sections and the same lines in the same order; each line carries every
 * column's figure, and each total is its own column's sum, never a figure
 * carried across (decision 0546).
 *
 * 🔨 A column whose shape differs is refused by name rather than zipped out of
 * step, because a figure printed on the wrong line is worse than no statement.
 */
export function mergeStatementColumns(columns: readonly { key: string; blocks: readonly StatementBlock[] }[]): ColumnedStatementBlock[] {
  const first = columns[0];
  if (!first) return [];
  return first.blocks.map((block, blockIndex) => ({
    heading: block.heading,
    lines: block.lines.map((line, lineIndex) => {
      const amounts: Record<string, number | null> = {};
      const credits: Record<string, boolean> = {};
      let kind = line.kind;
      for (const column of columns) {
        const other = column.blocks[blockIndex]?.lines[lineIndex];
        if (!other || other.label !== line.label || column.blocks[blockIndex]!.heading !== block.heading) {
          throw new Error(`the ${column.key} column of the statement does not line up with the first at ${block.heading}: ${line.label}`);
        }
        amounts[column.key] = other.amount;
        if (other.credit === true) credits[column.key] = true;
        // A parent reads as a heading only where it carries no figure in any column.
        if (kind === 'heading' && other.kind === 'account') kind = 'account';
      }
      // 🔒 A parent carrying a figure in one column reads zero, not blank, in a column where it carries none.
      if (kind === 'account') for (const key of Object.keys(amounts)) if (amounts[key] === null) amounts[key] = 0;
      const { amount: _amount, credit: _credit, ...rest } = line;
      void _amount;
      void _credit;
      return { ...rest, kind, amounts, ...(Object.keys(credits).length > 0 ? { credits } : {}) };
    }),
  }));
}
